import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet";
import {
  Users,
  Mail,
  FileText,
  DollarSign,
  Upload,
  Trash2,
  Download,
  LogOut,
  UserPlus,
  Copy,
  BarChart3,
  Menu,
  X,
  Eye,
  Clock,
  CheckCircle,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context helper
const getAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// NoIndex component for admin pages
const AdminNoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

// Admin Login Component
export const AdminLogin = () => {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Check if any admin exists
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/admin/check-setup`);
        setIsSetup(!response.data.needs_setup);
      } catch (error) {
        // Fallback: assume setup needed if check fails
        setIsSetup(false);
      }
      setLoading(false);
    };
    checkSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (isSetup) {
        response = await axios.post(`${API}/admin/login`, {
          email: formData.email,
          password: formData.password
        });
      } else {
        response = await axios.post(`${API}/admin/setup`, formData);
      }

      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.admin));
      toast.success(isSetup ? "Welcome back!" : "Admin account created!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <AdminNoIndex />
        <div className="animate-spin w-8 h-8 border-4 border-centerra-purple border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <AdminNoIndex />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-black">
            {isSetup ? "Admin Login" : "Create Admin Account"}
          </CardTitle>
          <CardDescription>
            {isSetup ? "Sign in to access the dashboard" : "Set up your first admin account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isSetup && (
              <Input
                data-testid="admin-name-input"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            )}
            <Input
              data-testid="admin-email-input"
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <Button
              data-testid="admin-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-centerra-purple hover:bg-centerra-purple/90"
            >
              {submitting ? "Please wait..." : isSetup ? "Sign In" : "Create Account"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Register with Invite
export const AdminRegister = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const validateInvite = async () => {
      try {
        await axios.get(`${API}/admin/invite/${token}`);
        setValid(true);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Invalid or expired invite");
        setValid(false);
      }
      setValidating(false);
    };
    validateInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/admin/register/${token}`, formData);
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.admin));
      toast.success("Account created successfully!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <AdminNoIndex />
        <div className="animate-spin w-8 h-8 border-4 border-centerra-purple border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <AdminNoIndex />
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Invalid Invite</h2>
            <p className="text-slate-600 mb-4">This invite link is invalid or has expired.</p>
            <Button onClick={() => navigate("/admin")} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <AdminNoIndex />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-black">Create Your Account</CardTitle>
          <CardDescription>You've been invited to join as an admin</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-centerra-purple hover:bg-centerra-purple/90"
            >
              {submitting ? "Creating Account..." : "Create Account"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard Component
export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [files, setFiles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [inviteHours, setInviteHours] = useState(48);
  const [newInvite, setNewInvite] = useState(null);

  const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      
      const [statsRes, contactsRes, newsletterRes, volunteersRes, donationsRes, filesRes, adminsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
        axios.get(`${API}/admin/volunteers`, { headers }),
        axios.get(`${API}/admin/donations`, { headers }),
        axios.get(`${API}/admin/files`, { headers }),
        axios.get(`${API}/admin/list`, { headers }),
        axios.get(`${API}/admin/invites`, { headers })
      ]);

      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setNewsletter(newsletterRes.data);
      setVolunteers(volunteersRes.data);
      setDonations(donationsRes.data);
      setFiles(filesRes.data);
      setAdmins(adminsRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/admin/files/upload`, formData, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
      });
      toast.success("File uploaded successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    
    try {
      await axios.delete(`${API}/admin/files/${fileId}`, { headers: getAuthHeader() });
      toast.success("File deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact submission?")) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, { headers: getAuthHeader() });
      toast.success("Contact deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm("Remove this subscriber?")) return;
    
    try {
      await axios.delete(`${API}/admin/newsletter/${subscriberId}`, { headers: getAuthHeader() });
      toast.success("Subscriber removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove subscriber");
    }
  };

  const handleDeleteVolunteer = async (volunteerId) => {
    if (!window.confirm("Delete this volunteer submission?")) return;
    
    try {
      await axios.delete(`${API}/admin/volunteers/${volunteerId}`, { headers: getAuthHeader() });
      toast.success("Volunteer deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete volunteer");
    }
  };

  const handleCreateInvite = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/invite`,
        { expires_in_hours: inviteHours },
        { headers: getAuthHeader() }
      );
      setNewInvite(response.data);
      toast.success("Invite link created!");
      fetchData();
    } catch (error) {
      toast.error("Failed to create invite");
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/admin/register/${newInvite.token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied!");
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const sidebarItems = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "contacts", label: "Contacts", icon: Mail },
    { id: "volunteers", label: "Volunteers", icon: Users },
    { id: "newsletter", label: "Newsletter", icon: Mail },
    { id: "donations", label: "Donations", icon: DollarSign },
    { id: "files", label: "Files", icon: FileText },
    { id: "admins", label: "Admins", icon: UserPlus }
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNoIndex />
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-bold text-black">Admin Dashboard</span>
        <button onClick={handleLogout} className="p-2 text-slate-500">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-black transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <span className="font-bold text-white">Centerra Admin</span>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="p-4 space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? "bg-centerra-purple text-white"
                  : "text-white/70 hover:bg-white/10"
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="text-white/70 text-sm mb-2">{adminUser.email}</div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 p-6">
        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div>
            <h1 className="text-2xl font-bold text-black mb-6">Dashboard Overview</h1>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-black">{stats.contacts}</p>
                      <p className="text-sm text-slate-500">Contacts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-black">{stats.volunteers || 0}</p>
                      <p className="text-sm text-slate-500">Volunteers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-black">{stats.newsletter_subscribers}</p>
                      <p className="text-sm text-slate-500">Subscribers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-black">${stats.total_donation_amount}</p>
                      <p className="text-sm text-slate-500">Donations</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-black">{stats.files}</p>
                      <p className="text-sm text-slate-500">Files</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Contacts</CardTitle>
                </CardHeader>
                <CardContent>
                  {contacts.slice(0, 5).map((contact) => (
                    <div key={contact.id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium text-black">{contact.name}</p>
                        <p className="text-sm text-slate-500">{contact.email}</p>
                      </div>
                      <span className="text-xs text-slate-400">{formatDate(contact.created_at)}</span>
                    </div>
                  ))}
                  {contacts.length === 0 && <p className="text-slate-500 text-center py-4">No contacts yet</p>}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Subscribers</CardTitle>
                </CardHeader>
                <CardContent>
                  {newsletter.slice(0, 5).map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <p className="text-black">{sub.email}</p>
                      <span className="text-xs text-slate-400">{formatDate(sub.subscribed_at)}</span>
                    </div>
                  ))}
                  {newsletter.length === 0 && <p className="text-slate-500 text-center py-4">No subscribers yet</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div>
            <h1 className="text-2xl font-bold text-black mb-6">Contact Form Submissions</h1>
            <Card>
              <CardContent className="pt-6">
                {contacts.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No contact submissions yet</p>
                ) : (
                  <div className="space-y-4">
                    {contacts.map((contact) => (
                      <div key={contact.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-black">{contact.name}</p>
                            <p className="text-sm text-slate-500">{contact.email}</p>
                            {contact.organization && (
                              <p className="text-sm text-slate-400">{contact.organization}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{formatDate(contact.created_at)}</span>
                            <button
                              onClick={() => handleDeleteContact(contact.id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-slate-700 bg-slate-50 p-3 rounded">{contact.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === "newsletter" && (
          <div>
            <h1 className="text-2xl font-bold text-black mb-6">Newsletter Subscribers ({newsletter.length})</h1>
            <Card>
              <CardContent className="pt-6">
                {newsletter.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No subscribers yet</p>
                ) : (
                  <div className="divide-y">
                    {newsletter.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between py-3">
                        <p className="text-black">{sub.email}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-400">{formatDate(sub.subscribed_at)}</span>
                          <button
                            onClick={() => handleDeleteSubscriber(sub.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Volunteers Tab */}
        {activeTab === "volunteers" && (
          <div>
            <h1 className="text-2xl font-bold text-black mb-6">Volunteer Submissions ({volunteers.length})</h1>
            <Card>
              <CardContent className="pt-6">
                {volunteers.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No volunteer submissions yet</p>
                ) : (
                  <div className="space-y-4">
                    {volunteers.map((volunteer) => (
                      <div key={volunteer.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-black text-lg">{volunteer.name}</p>
                            <p className="text-sm text-slate-500">{volunteer.email} • {volunteer.phone}</p>
                            <p className="text-sm text-slate-400">{volunteer.district}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{formatDate(volunteer.created_at)}</span>
                            <button
                              onClick={() => handleDeleteVolunteer(volunteer.id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded space-y-2">
                          <p className="text-sm">
                            <span className="font-medium text-slate-700">Activity Interest:</span>{" "}
                            <span className="text-slate-600">{volunteer.activity}</span>
                          </p>
                          {volunteer.other_activity && (
                            <p className="text-sm">
                              <span className="font-medium text-slate-700">Other:</span>{" "}
                              <span className="text-slate-600">{volunteer.other_activity}</span>
                            </p>
                          )}
                          {volunteer.comments && (
                            <p className="text-sm">
                              <span className="font-medium text-slate-700">Comments:</span>{" "}
                              <span className="text-slate-600">{volunteer.comments}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Donations Tab */}
        {activeTab === "donations" && (
          <div>
            <h1 className="text-2xl font-bold text-black mb-6">Donations</h1>
            <Card>
              <CardContent className="pt-6">
                {donations.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No donations yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">Donor</th>
                          <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">Amount</th>
                          <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">Status</th>
                          <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">Type</th>
                          <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations.map((donation) => (
                          <tr key={donation.id} className="border-b last:border-0">
                            <td className="py-3 px-2">
                              <p className="font-medium text-black">{donation.donor_name || "Anonymous"}</p>
                              <p className="text-sm text-slate-500">{donation.donor_email || "-"}</p>
                            </td>
                            <td className="py-3 px-2 font-semibold text-black">${donation.amount}</td>
                            <td className="py-3 px-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                donation.payment_status === "completed"
                                  ? "bg-green-100 text-green-700"
                                  : donation.payment_status === "pending"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700"
                              }`}>
                                {donation.payment_status === "completed" && <CheckCircle className="w-3 h-3" />}
                                {donation.payment_status === "pending" && <Clock className="w-3 h-3" />}
                                {donation.payment_status}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-sm text-slate-600">
                              {donation.is_recurring ? "Monthly" : "One-time"}
                            </td>
                            <td className="py-3 px-2 text-sm text-slate-500">{formatDate(donation.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === "files" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-black">Files & Documents</h1>
              <div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                />
                <Button 
                  disabled={uploading} 
                  className="bg-centerra-purple hover:bg-centerra-purple/90"
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload File"}
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="pt-6">
                {files.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No files uploaded yet</p>
                ) : (
                  <div className="grid gap-4">
                    {files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-black">{file.original_filename}</p>
                            <p className="text-sm text-slate-500">{formatFileSize(file.file_size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${BACKEND_URL}${file.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-slate-500 hover:bg-slate-100 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={`${BACKEND_URL}${file.url}`}
                            download
                            className="p-2 text-slate-500 hover:bg-slate-100 rounded"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === "admins" && (
          <div>
            <h1 className="text-2xl font-bold text-black mb-6">Admin Management</h1>
            
            {/* Create Invite */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Invite New Admin</CardTitle>
                <CardDescription>Generate a link to invite someone as an admin</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Expires in:</span>
                    <select
                      value={inviteHours}
                      onChange={(e) => setInviteHours(Number(e.target.value))}
                      className="border rounded px-3 py-2"
                    >
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                      <option value={72}>72 hours</option>
                      <option value={168}>1 week</option>
                    </select>
                  </div>
                  <Button onClick={handleCreateInvite} className="bg-centerra-purple hover:bg-centerra-purple/90">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Invite
                  </Button>
                </div>
                
                {newInvite && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-green-800 mb-2">Invite link created!</p>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={`${window.location.origin}/admin/register/${newInvite.token}`}
                        className="bg-white"
                      />
                      <Button onClick={copyInviteLink} variant="outline">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-green-600 mt-2">Expires: {formatDate(newInvite.expires_at)}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Admin List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Current Admins ({admins.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {admins.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-medium text-black">{admin.name}</p>
                        <p className="text-sm text-slate-500">{admin.email}</p>
                      </div>
                      <span className="text-xs text-slate-400">Joined {formatDate(admin.created_at)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};
